import type { Result } from "neverthrow";
import { err, ok } from "neverthrow";

import type { ButterflowWorkflow } from "./workflow-butterflow";

export interface WorkflowValidationError {
  code: "MULTIPLE_NODES" | "NO_NODES";
  message: string;
}

/**
 * Validates that a workflow has exactly one node.
 * We only support single-node workflows for campaign creation.
 */
export function validateSingleNodeWorkflow(
  workflow: ButterflowWorkflow,
): Result<true, WorkflowValidationError> {
  if (workflow.nodes.length === 0) {
    return err({
      code: "NO_NODES",
      message: "Workflow must have at least one node",
    });
  }

  if (workflow.nodes.length > 1) {
    return err({
      code: "MULTIPLE_NODES",
      message:
        "We only support single-node workflows for campaign creation. Multi-node workflows are not supported yet. We automatically add a step to create pull requests after your workflow runs.",
    });
  }

  return ok(true);
}

/**
 * Type guard to check if a workflow validation error is a multi-node error
 */
export function isMultiNodeError(error: WorkflowValidationError): boolean {
  return error.code === "MULTIPLE_NODES";
}

/**
 * Gets a user-friendly error message for workflow validation errors
 */
export function getWorkflowValidationErrorMessage(
  error: WorkflowValidationError,
): string {
  switch (error.code) {
    case "MULTIPLE_NODES":
      return "This workflow has multiple nodes, but we only support single-node workflows for campaign creation. We automatically add a step to create pull requests after your workflow runs.";
    case "NO_NODES":
      return "This workflow doesn't contain any nodes. Please select a valid workflow.";
    default:
      return "Invalid workflow configuration.";
  }
}
